import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession } from './auth-client';
import { api, type DashboardState } from './api';
import { AppShell } from './layout/app-shell';
import { LoginPage } from './pages/login';
import { RunsPage } from './pages/runs';
import { TenantsPage } from './pages/tenants';
import { UsersPage } from './pages/users';
import { AcceptPage } from './pages/accept';
import { AccountPage } from './pages/account';

export function App() {
  const session = useSession();
  const [tenant, setTenant] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ['dashboard', tenant],
    queryFn: () => api.get<DashboardState>(`/api/dashboard${tenant ? `?tenant=${tenant}` : ''}`),
    enabled: Boolean(session.data),
  });

  if (session.isPending) return null;
  // The accept page is the one surface reachable before an account exists: an
  // invited address with no account sets its password there.
  if (!session.data) {
    return window.location.pathname.startsWith('/accept/') ? (
      <Routes>
        <Route path="/accept/:id" element={<AcceptPage />} />
      </Routes>
    ) : (
      <LoginPage />
    );
  }

  const isAdmin = (session.data.user as { role?: string }).role === 'admin';

  return (
    <AppShell
      email={session.data.user.email}
      isAdmin={isAdmin}
      tenants={state.data?.tenants ?? []}
      tenant={tenant}
      onTenant={setTenant}
    >
      <Routes>
        <Route path="/" element={<RunsPage tenant={tenant} />} />
        <Route path="/tenants" element={<TenantsPage tenant={tenant} isAdmin={isAdmin} />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/accept/:id" element={<AcceptPage signedInAs={session.data.user.email} />} />
      </Routes>
    </AppShell>
  );
}
